// components/VideoControls.tsx
import FeatherIcon from 'feather-icons-react';
import React from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';

interface VideoControlsProps {
  playbackSpeed: number;
  isPlaying: boolean;
  dragModeEnabled: boolean;
  gpsDisplacementEnabled: boolean;
  gpsDisplacementResult: any;
  onSpeedChange: (speed: number) => void;
  onNavigateFrames: (frames: number) => void;
  onTogglePlayPause: () => void;
  onToggleDragMode: () => void;
  onToggleGPSDisplacement: () => void;
}

const VideoControls: React.FC<VideoControlsProps> = ({
  playbackSpeed,
  isPlaying,
  dragModeEnabled,
  gpsDisplacementEnabled,
  gpsDisplacementResult,
  onSpeedChange,
  onNavigateFrames,
  onTogglePlayPause,
  onToggleDragMode,
  onToggleGPSDisplacement
}) => {
  return (
    <>
      {/* Controles Principais - Navegação do Vídeo */}
      <Card className="mb-2">
        <Card.Body className="py-2">
          <div className="d-flex justify-content-between align-items-center gap-1 flex-wrap">
            {/* Velocidade */}
            <Form.Select
              size="sm"
              value={playbackSpeed}
              onChange={e => onSpeedChange(Number(e.target.value))}
              style={{ width: 'auto', minWidth: '80px' }}
            >
              <option value={0.25}>0.25x</option>
              <option value={0.5}>0.5x</option>
              <option value={0.75}>0.75x</option>
              <option value={1}>1x</option>
              <option value={1.25}>1.25x</option>
              <option value={1.5}>1.5x</option>
              <option value={2}>2x</option>
              <option value={3}>3x</option> {/* NOVO */}
              <option value={4}>4x</option> {/* NOVO */}
              <option value={5}>5x</option> {/* NOVO */}
            </Form.Select>

            {/* Navegação -5 frames */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onNavigateFrames(-5)}
              title="5 quadros anteriores"
            >
              <FeatherIcon icon="chevrons-left" size={14} />
              <small className="ms-1">5</small>
            </Button>

            {/* Navegação -1 frame */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onNavigateFrames(-1)}
              title="1 quadro anterior"
            >
              <FeatherIcon icon="chevron-left" size={14} />
              <small className="ms-1">1</small>
            </Button>

            {/* Play/Pause */}
            <Button
              variant="primary"
              size="sm"
              onClick={onTogglePlayPause}
              title="Play/Pause"
              className="px-3"
            >
              <FeatherIcon icon={isPlaying ? 'pause' : 'play'} size={16} />
            </Button>

            {/* Navegação +1 frame */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onNavigateFrames(1)}
              title="1 quadro seguinte"
            >
              <small className="me-1">1</small>
              <FeatherIcon icon="chevron-right" size={14} />
            </Button>

            {/* Navegação +5 frames */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onNavigateFrames(5)}
              title="5 quadros seguintes"
            >
              <small className="me-1">5</small>
              <FeatherIcon icon="chevrons-right" size={14} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Alert Modo Drag */}
      {dragModeEnabled && (
        <Alert variant="warning" className="mb-2 py-2">
          <div className="d-flex align-items-center justify-content-between">
            <small className="mb-0">
              <FeatherIcon icon="move" size={14} className="me-2" />
              <strong>Modo de Movimentação Ativo:</strong> Arraste os marcadores
              no vídeo
            </small>
            <Button
              variant="outline-warning"
              size="sm"
              onClick={onToggleDragMode}
            >
              Desativar
            </Button>
          </div>
        </Alert>
      )}

      {/* Alert GPS Displacement */}
      {gpsDisplacementEnabled && (
        <Alert variant="info" className="mb-2 py-2">
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ flex: 1 }}>
              <div
                className="mb-1 d-block"
                style={{ color: '#004085', fontSize: '0.9rem' }}
              >
                <FeatherIcon
                  icon="crosshair"
                  size={14}
                  className="me-2"
                  style={{ verticalAlign: 'middle' }}
                />
                <strong>Modo de Ajuste GPS Ativo:</strong> Mova o mouse sobre o
                vídeo
              </div>
              <div
                className="d-block"
                style={{
                  color: '#31708f',
                  fontSize: '0.85rem',
                  lineHeight: '1.6'
                }}
              >
                • <strong style={{ color: '#004085' }}>Horizontal (←→):</strong>{' '}
                Deslocamento lateral (máx. 10m)
                <br />•{' '}
                <strong style={{ color: '#004085' }}>Vertical (↑):</strong>{' '}
                Profundidade à frente (0-7m)
              </div>
              {gpsDisplacementResult && (
                <div
                  className="mt-2 p-2 rounded border"
                  style={{ backgroundColor: '#f0f8ff', borderColor: '#bee5eb' }}
                >
                  <div
                    className="d-block mb-1"
                    style={{ color: '#004085', fontSize: '0.85rem' }}
                  >
                    <strong>Ajuste Atual:</strong>
                  </div>
                  <div
                    className="d-block"
                    style={{
                      color: '#31708f',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}
                  >
                    H: {gpsDisplacementResult.horizontalOffset.toFixed(2)}m | D:{' '}
                    {gpsDisplacementResult.depthOffset.toFixed(2)}m
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="outline-info"
              size="sm"
              onClick={onToggleGPSDisplacement}
              style={{ whiteSpace: 'nowrap' }}
            >
              Desativar
            </Button>
          </div>
        </Alert>
      )}
    </>
  );
};

export default VideoControls;
